import React from 'react';
import './StateView.css';

export type StateViewProps =
    | { state: 'loading'; label?: string; lines?: number; children?: React.ReactNode }
    | { state: 'empty'; title: string; message?: React.ReactNode; action?: React.ReactNode }
    | { state: 'filtered-empty'; title?: string; message?: React.ReactNode; onClearFilters: () => void }
    | { state: 'error'; title?: string; message: React.ReactNode; onRetry?: () => void; retrying?: boolean }
    | { state: 'denied'; title?: string; reason: React.ReactNode; nextStep: React.ReactNode; action?: React.ReactNode }
    | { state: 'partial'; message: React.ReactNode; onRetry?: () => void; retrying?: boolean; children: React.ReactNode };

/**
 * One truthful component for the non-populated states of a collection, metric or
 * form (spec §13): loading, empty, filtered-empty, error, denied and partial are
 * visibly different, and "no data" is never shown for "failed to load".
 * `loading` children replace the default skeleton lines with a layout-shaped one.
 */
export function StateView(props: StateViewProps) {
    switch (props.state) {
        case 'loading':
            return (
                <div role="status" aria-busy="true" aria-label={props.label ?? 'Loading'} className="ui-state ui-state--loading">
                    {props.children ?? Array.from({ length: props.lines ?? 3 }, (_, i) => (
                        <div key={i} className="skeleton ui-state-line" aria-hidden="true" />
                    ))}
                </div>
            );
        case 'empty':
            return <Panel kind="empty" title={props.title} body={props.message} actions={props.action} />;
        case 'filtered-empty':
            return (
                <Panel kind="filtered-empty" title={props.title ?? 'No results match these filters'}
                    body={props.message ?? 'Change or clear the filters to see more.'}
                    actions={<button type="button" className="btn btn-secondary btn-md" onClick={props.onClearFilters}>Clear filters</button>} />
            );
        case 'error':
            return (
                <Panel kind="error" role="alert" title={props.title ?? "This couldn't be loaded"} body={props.message}
                    actions={props.onRetry && <Retry onRetry={props.onRetry} retrying={props.retrying} />} />
            );
        case 'denied':
            return (
                <Panel kind="denied" title={props.title ?? "You don't have access"}
                    body={<><span className="ui-state-reason">{props.reason}</span> <span>{props.nextStep}</span></>}
                    actions={props.action} />
            );
        case 'partial':
            return (
                <div className="ui-state--partial">
                    <div role="alert" className="ui-state-notice">
                        <span>{props.message}</span>
                        {props.onRetry && <Retry onRetry={props.onRetry} retrying={props.retrying} />}
                    </div>
                    {props.children}
                </div>
            );
    }
}

function Panel({ kind, role, title, body, actions }: {
    kind: string; role?: 'alert'; title: string; body?: React.ReactNode; actions?: React.ReactNode;
}) {
    return (
        <div role={role} className={`ui-state ui-state--${kind}`}>
            <p className="ui-state-title">{title}</p>
            {body && <p className="ui-state-body">{body}</p>}
            {actions && <div className="ui-state-actions">{actions}</div>}
        </div>
    );
}

function Retry({ onRetry, retrying }: { onRetry: () => void; retrying?: boolean }) {
    return (
        <button type="button" className="btn btn-secondary btn-md" onClick={onRetry} disabled={retrying} aria-busy={retrying || undefined}>
            Retry
        </button>
    );
}
